// Example: @/types/transcriptionSessionTypes.ts

/**
 * Represents AI Insight types
 */
export enum AIInsightType {
  ActionItem = 'ActionItem',
  KeyPoint = 'KeyPoint',
  Sentiment = 'Sentiment',
  Concern = 'Concern',
  Opportunity = 'Opportunity'
}

/**
 * Represents AI Insight priority levels
 */
export enum AIInsightPriority {
  High = 'High',
  Medium = 'Medium',
  Low = 'Low'
}

/**
 * Represents transcription session status
 */
export enum TranscriptionSessionStatus {
  Draft = 0,
  Recording = 1,
  Processing = 2,
  PendingApproval = 3,
  Approved = 4,
  Rejected = 5
}

/**
 * Represents approval status for transcription sessions
 */
export enum ApprovalStatus {
  NotRequired = 0, // Session doesn't need approval
  Pending = 1, // Awaiting approval
  Approved = 2, // Approved by authorized user
  Rejected = 3, // Rejected with optional notes
  Expired = 4 // Approval period expired (future feature)
}

/**
 * Represents a speaker segment as returned by the backend.
 * Based on C# SpeakerSegmentResult.
 */
export interface SpeakerSegmentResult {
  id: string; // GUID
  speakerIndex: number;
  textContent: string;
  normalizedTextContent: string;
  timestamp: string; // ISO 8601 DateTimeOffset string (e.g., "2023-10-27T14:30:00Z")
  confidenceScore: number;
  durationSeconds: number;
  isPauseSegment?: boolean;
  createdAt: string; // ISO 8601 DateTime string
  updatedAt?: string | null; // ISO 8601 DateTime string or null
}

// Based on GetAllTranscriptionSessionsInput from C# (UserId added by service call)
export interface GetAllTranscriptionSessionsParams {
  userId: string; // For X-User-Id header primarily
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortAscending?: boolean;
  searchQuery?: string;
  personId?: string; // Optional filter by person ID
  createdByUserId?: string; // Optional filter by creator user ID
  // Add other filters if your API supports them (e.g., date ranges)
}

/**
 * Paginated response for transcription sessions
 */
export interface PaginatedTranscriptionSessionsResponse {
  sessions: TranscriptionSession[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Represents an AI-generated insight as returned by the backend.
 * Based on C# AIInsightResult.
 * Note: Enum string values from backend (e.g., "ActionItem", "High")
 */
export interface AIInsightResult {
  id: string; // GUID
  type: string; // e.g., "ActionItem", "Summary", "KeyMoment", "Question", "Sentiment", "Concern", "Opportunity"
  title: string;
  normalizedTitle: string;
  description: string;
  normalizedDescription: string;
  priority: string; // e.g., "Low", "Medium", "High", "Critical"
  timestamp: string; // ISO 8601 DateTimeOffset string
  relatedSpeakerIndex?: number | null;
  createdAt: string; // ISO 8601 DateTime string
  updatedAt?: string | null; // ISO 8601 DateTime string or null
}

/**
 * Represents a template question and its status as returned by the backend.
 * Based on C# SessionTemplateQuestionResult.
 */
export interface SessionTemplateQuestionResult {
  id: string; // GUID
  questionText: string;
  normalizedQuestionText: string;
  answerText?: string | null;
  normalizedAnswerText?: string | null;
  confidenceScore?: number | null;
  isComplete: boolean;
  category: string;
  normalizedCategory: string;
  createdAt: string; // ISO 8601 DateTime string
  updatedAt?: string | null; // ISO 8601 DateTime string or null
  isRequired?: boolean;
}

/**
 * Represents a key topic extracted from the session as returned by the backend.
 * Based on C# SessionKeyTopicResult.
 */
export interface SessionKeyTopicResult {
  id: string; // GUID
  topicText: string;
  normalizedTopicText: string;
  createdAt: string; // ISO 8601 DateTime string
  updatedAt?: string | null; // ISO 8601 DateTime string or null
}

// New: Recording summary returned with a session
export interface RecordingResultDto {
  id: string;
  recordingStartTimeUtc: string;
  recordingEndTimeUtc?: string | null;
  transcriptionDocumentId?: string | null;
  orderIndex?: number | null;
}

// New: Document summary returned with a session
export interface DocumentResult {
  id: string;
  documentName: string;
  documentUrl: string;
  fileFormat: string;
  fileSizeBytes: number;
  documentType: number;
  documentStatus: number;
  createdAt: string;
  firstChunkText?: string | null;
  fullText?: string | null;
}

/**
 * Represents session metrics as returned by the backend.
 * Based on C# SessionMetricsResult.
 */
export interface SessionMetricsResult {
  id: string; // GUID
  recordedDuration: string; // TimeSpan from C# might be serialized as ISO 8601 Duration (e.g., "PT2M30S") or a custom string like "00:02:30.123". Client will need to parse.
  wordCount: number;
  speakerCount: number;
  averageConfidenceScore: number;
  overallSentiment: string; // e.g., "Neutral", "Positive", "Negative", "Mixed", "Unknown"
  keyTopics: SessionKeyTopicResult[];
  createdAt: string; // ISO 8601 DateTime string
  updatedAt?: string | null; // ISO 8601 DateTime string or null
}

/**
 * Represents the full transcription session data as returned by the backend.
 * This would be the type for the result of `getTranscriptionSessionById`.
 * Based on C# TranscriptionSessionResult.
 */
// Simple user result for transcription sessions
export interface TranscriptionSessionUser {
  id: string; // GUID
  fullName: string; // DisplayName or Email from backend
}

export interface SessionCollectionInfo {
  id: string;
  name: string;
  description?: string | null;
  referenceNumber?: string | null;
}

export interface TranscriptionSession {
  id: string; // GUID
  title?: string | null;
  normalizedTitle?: string | null;
  userId: string; // GUID of the owner/creator
  user?: TranscriptionSessionUser; // User who created the session
  companyId: string; // GUID
  // company: BackendCompanyResult; // Define if needed, based on C# CompanyResult
  templateId?: number | null;
  // template?: BackendTemplateInfoResult; // Define if needed, based on C# TemplateInfoResult
  sessionStartTime: string; // ISO 8601 DateTimeOffset string
  sessionEndTime?: string | null; // ISO 8601 DateTimeOffset string or null
  status: TranscriptionSessionStatus;
  fullTranscriptText: string;
  normalizedTranscriptText: string;
  speakerSegments: SpeakerSegmentResult[];
  aiInsights: AIInsightResult[];
  templateQuestions: SessionTemplateQuestionResult[];
  metrics?: SessionMetricsResult | null;
  createdAt: string; // ISO 8601 DateTime string
  updatedAt?: string | null; // ISO 8601 DateTime string or null
  audioUrl?: string; // Legacy field
  primaryAudioDocumentId?: string;
  userThreadId?: string | null;
  recordings?: RecordingResultDto[];
  companyDocuments?: DocumentResult[];
  collections?: SessionCollectionInfo[]; // Collections this session belongs to

  // Approval-related fields
  approvalStatus?: ApprovalStatus;
  approvedByUserId?: string | null;
  approvedAt?: string | null; // ISO 8601 DateTime string
  approvalNotes?: string | null;
  requiresApproval?: boolean;

  gatingLastProcessedSegmentId?: string | null;
  gatingLastProcessedAtUtc?: string | null;
  gatingLastProcessedRecordingId?: string | null;
  gatingLastDeltaHash?: string | null;
  gatingLastHeavyTriggeredAtUtc?: string | null;

  notes?: string | null; // User notes for the session

  summaries?: GeneratedSummary[];
}

export interface GeneratedSummary {
  id: string;
  templateSummaryId: number;
  sessionId: string;
  name: string;
  audience: string;
  content: string;
  generatedAt: string;
}

// --- Live collaboration event types ---
export type TranscriptionLiveEventType =
  | 'recording-state'
  | 'segment-added'
  | 'recording-divider'
  | 'recording-stopped';

export interface RecordingStatePayload {
  recordingId?: string | null;
  isRecording: boolean;
  isPaused: boolean;
  accumulatedMilliseconds: number;
  durationMilliseconds?: number | null;
  audioLevel?: number;
  interimTranscript?: string | null;
  recordingStartedAtUtc?: string | null;
  recordingEndedAtUtc?: string | null;
  updatedAtUtc?: string | null;
}

export interface LiveSegmentEventPayload {
  id: string;
  speakerIndex: number;
  textContent: string;
  timestamp: string;
  confidenceScore: number;
  durationSeconds: number;
  transcriptionRecordingId?: string | null;
  isPauseSegment?: boolean;
}

export interface RecordingDividerPayload {
  recordingId: string;
  startedAtUtc: string;
}

export interface RecordingStoppedPayload {
  recordingId: string;
  stoppedAtUtc: string;
}

export interface TranscriptionLiveEvent<T = any> {
  event: TranscriptionLiveEventType;
  payload: T;
}

export interface SegmentsDeltaResponse {
  segments: SpeakerSegmentResult[];
  nextCursor?: string | null;
  count: number;
}

// --- Client-Side Specific Types (Used in React Component State) ---

/**
 * Represents a speaker segment as used in the client-side React component's state.
 */
export interface ClientSpeakerSegment {
  id: string; // Can be a temporary client-generated ID or backend GUID
  speaker: number; // Corresponds to BackendSpeakerSegment.speakerIndex
  text: string; // Corresponds to BackendSpeakerSegment.textContent
  timestamp: string; // Formatted time string for display (e.g., "HH:MM:SS")
  confidence: number; // Corresponds to BackendSpeakerSegment.confidenceScore
  duration: number; // Corresponds to BackendSpeakerSegment.durationSeconds
  isPauseSegment?: boolean;
}

/**
 * Represents an AI-generated insight as used in the client-side React component's state.
 */
export interface ClientAIInsight {
  id: string;
  type: 'action_item' | 'key_point' | 'sentiment' | 'concern' | 'opportunity';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  timestamp: string; // Formatted time string for display
  relatedSpeaker?: number; // Corresponds to BackendAIInsight.relatedSpeakerIndex
}

/**
 * Represents a template question as used in the client-side React component's state.
 */
export interface ClientTemplateQuestion {
  id: string; // Corresponds to BackendSessionTemplateQuestion.id
  question: string; // Corresponds to BackendSessionTemplateQuestion.questionText
  answer: string; // Corresponds to BackendSessionTemplateQuestion.answerText
  confidence: number; // Corresponds to BackendSessionTemplateQuestion.confidenceScore
  isComplete: boolean; // Corresponds to BackendSessionTemplateQuestion.isComplete
  category: string; // Corresponds to BackendSessionTemplateQuestion.category
}

/**
 * Represents session metrics as used in the client-side React component's state.
 */
export interface ClientSessionMetrics {
  duration: string; // Formatted duration string (e.g., "HH:MM:SS")
  wordCount: number;
  speakerCount: number;
  avgConfidence: number;
  keyTopics: string[]; // Array of topic strings, mapped from BackendSessionKeyTopic[].topicText
  sentiment: 'positive' | 'neutral' | 'negative' | 'mixed' | 'unknown'; // Mapped from BackendSessionMetrics.overallSentiment
}

/**
 * Defines the possible active tabs in the UI.
 */
export type ActiveTabType =
  | 'transcript'
  | 'summaries'
  | 'template'
  | 'documents'
  | 'speakers'
  | 'notes'
  | 'chat'
  | 'critical';

// --- Client-Side Input DTOs (for API service calls) ---
// (These would typically be here or in a separate file for client service inputs)

export interface CreateTranscriptionSessionClientInput {
  title?: string | null;
  templateId?: number | null;
  sessionStartTime: string; // ISO DateTime string
  fullTranscriptText?: string | null; // Optional: backend might initialize
  collectionId?: string | null; // Optional: ID of existing collection to add session to
  collectionName?: string | null; // Optional: collection name to create new or find existing collection
}

export interface AddSpeakerSegmentClientInput {
  // SessionId is part of the URL path in the service call
  textContent: string;
  timestamp: string; // ISO DateTime string
  speakerIndex?: number | null;
  confidenceScore?: number | null;
  durationSeconds?: number | null;
  isPauseSegment?: boolean | null;
}

export interface UpdateTranscriptionSessionClientInput {
  id: string; // Session ID to update
  title?: string | null;
  sessionEndTime?: string | null; // ISO DateTime string
  fullTranscriptText?: string | null; // To update the full transcript if needed
  status?: TranscriptionSessionStatus; // Session status
  requiresApproval?: boolean; // Allow toggling approval requirement
  notes?: string | null; // User notes for the session
  // Add other updatable fields as necessary
}

// --- Client-Side Result DTOs (for API service calls) ---
// (These would typically be here or in a separate file for client service results)

export interface AddSpeakerSegmentResult {
  success: boolean;
  message?: string | null;
  sessionId: string; // GUID
  speakerSegmentId?: string | null; // GUID of the newly created segment
}

export interface PerformAiAnalysisResult {
  success: boolean;
  message?: string | null;
  sessionId: string; // GUID
  newAIInsightIds: string[]; // GUIDs
  updatedSessionTemplateQuestionIds: string[]; // GUIDs
  newSessionKeyTopicIds: string[]; // GUIDs
}

// NEW: Semantic Kernel Agent Types

/**
 * Result from the Semantic Kernel agent's real-time processing
 */
export interface RealtimeAgentResult {
  success: boolean;
  message?: string | null;
  sessionId: string; // GUID
  agentResponse?: string | null;
  actionsPerformed: string[];
}

/**
 * Streaming update from the Semantic Kernel agent
 */
export interface StreamingAgentUpdate {
  type: string; // e.g., "analysis", "error", "insight", "question_answered"
  content: string;
  timestamp: string; // ISO 8601 DateTime string
  metadata?: Record<string, any> | null;
}

/**
 * Input for processing a segment with the Semantic Kernel agent
 */
export interface AgentProcessSegmentInput {
  textContent: string;
  timestamp: string; // ISO DateTime string
  speakerIndex?: number | null;
  confidenceScore?: number | null;
  durationSeconds?: number | null;
  isPauseSegment?: boolean | null;
}

// --- Auth0 Related Types ---
// (Ensure these are correctly imported or defined if used directly in this file)
// import { GetTokenWithPopupOptions, PopupConfigOptions } from '@auth0/auth0-react';

export type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
export type GetAccessTokenWithPopupType = (
  options?: any, // Replace 'any' with GetTokenWithPopupOptions if imported
  config?: any // Replace 'any' with PopupConfigOptions if imported
) => Promise<string | undefined>;

// Helper functions for TranscriptionSessionStatus
export const getSessionStatusLabel = (
  status: TranscriptionSessionStatus
): string => {
  const labels = {
    [TranscriptionSessionStatus.Draft]: 'Draft',
    [TranscriptionSessionStatus.Recording]: 'Recording',
    [TranscriptionSessionStatus.Processing]: 'Processing',
    [TranscriptionSessionStatus.PendingApproval]: 'Pending Approval',
    [TranscriptionSessionStatus.Approved]: 'Approved',
    [TranscriptionSessionStatus.Rejected]: 'Rejected'
  };
  return labels[status] || 'Unknown';
};

export const getSessionStatusColor = (
  status: TranscriptionSessionStatus
): string => {
  const colors = {
    [TranscriptionSessionStatus.Draft]: '#9E9E9E',
    [TranscriptionSessionStatus.Recording]: '#EF4444',
    [TranscriptionSessionStatus.Processing]: '#FF9800',
    [TranscriptionSessionStatus.PendingApproval]: '#3B82F6',
    [TranscriptionSessionStatus.Approved]: '#22C55E',
    [TranscriptionSessionStatus.Rejected]: '#EF4444'
  };
  return colors[status] || '#9E9E9E';
};

// Helper functions for ApprovalStatus
export const getApprovalStatusLabel = (status: ApprovalStatus): string => {
  const labels = {
    [ApprovalStatus.NotRequired]: 'No Approval Required',
    [ApprovalStatus.Pending]: 'Pending Approval',
    [ApprovalStatus.Approved]: 'Approved',
    [ApprovalStatus.Rejected]: 'Rejected',
    [ApprovalStatus.Expired]: 'Expired'
  };
  return labels[status] || 'Unknown';
};

export const getApprovalStatusColor = (status: ApprovalStatus): string => {
  const colors = {
    [ApprovalStatus.NotRequired]: '#9E9E9E', // Grey - neutral
    [ApprovalStatus.Pending]: '#FF9800', // Orange - needs attention
    [ApprovalStatus.Approved]: '#4CAF50', // Green - success
    [ApprovalStatus.Rejected]: '#F44336', // Red - rejected
    [ApprovalStatus.Expired]: '#795548' // Brown - expired
  };
  return colors[status] || '#9E9E9E';
};

// Approval action types
export interface ApprovalActionRequest {
  sessionId: string;
  notes?: string;
}
